<template>
    <div class="services-autocomplete">
        <form-wrapper :validator="$v" class="form-wrapper">
            <form-group name="selectedAutocomplete" :label="label" attribute="Service">
                <md-input
                    ref="servicesAutocomplete"
                    type="text"
                    v-model="autocompleteText"
                    v-click-outside="handleClickOutside"
                    @focus="onFocus($event)"
                    @blur="blur"
                    @change="onChange"
                    @keyup.down="onArrowDown"
                    @keyup.up="onArrowUp"
                    @keyup.enter="onEnter"
                    :required="isRequired"
                    v-focus="shouldFocus"
                />
            </form-group>
        </form-wrapper>
        <div class="autocomplete">
            <ul
                id="autocomplete-results"
                v-show="isOpen && autocompleteList.length"
                class="autocomplete-results"
                ref="autocompleteContainer"
            >
                <li class="loading" v-if="isAutoCompleteLoading">Loading results...</li>
                <li
                    ref="autocompleteOptions"
                    v-else
                    v-for="(result, i) in autocompleteList"
                    :key="result.serviceId"
                    @click="setAutocompleteResult(result)"
                    class="autocomplete-result"
                    :class="{ 'is-active': i === arrowCounter }"
                >
                    <span class="code" v-if="result.serviceCode">({{ result.serviceCode }})</span>
                    <span class="sep" v-if="result.serviceCode">&nbsp;</span>
                    <span class="name">{{ result.name }}</span>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import _ from 'lodash';
import { handleRequests, showErrorMessage } from '@/helpers';

export default {
    name: 'ServicesAutocomplete',
    props: {
        id: { type: String, required: true },
        classname: { type: String, default: '' },
        label: { type: String, default: 'Service' },
        icon: { type: String, default: '' },
        shouldFocus: { type: Boolean, default: false },
        value: {
            type: Object,
            default() {
                return { serviceId: null };
            }
        },
        isRequired: { type: Boolean, default: true }
    },
    validations: {
        selectedAutocomplete: {
            required: (value, vm) => {
                if (!vm.isRequired) 
                    return true;
                if (vm.selectedAutocomplete === null) 
                    return false;
                return true;
            }
        }
    },
    data() {
        return {
            autocompleteText: null,
            autocompleteList: [],
            isOpen: false,
            isAutoCompleteLoading: false,
            arrowCounter: 0,
            selectedAutocomplete: null
        };
    },
    computed: { ...mapGetters({ user: 'user/user' }) },
    watch: {
        autocompleteText: _.debounce(function handleAutocompleteTextChange() {
            if (
                this.selectedAutocomplete !== null &&
                this.autocompleteText !== this.getDisplayText(this.selectedAutocomplete)
            )
                this.selectedAutocomplete = null;
            if (this.selectedAutocomplete === null) 
                this.getServicesList();
        }, 500)
    },
    async mounted() {
        if (this.value.serviceId != null) {
            await this.getServiceDetails(this.value.serviceId);
            this.update(this.getDisplayText(this.selectedAutocomplete));
        } else {
            this.update('');
        }
    },
    methods: {
        getDisplayText(svc) {
            if (!svc) 
                return '';
            return svc.serviceCode ? `(${svc.serviceCode}) ${svc.name}` : svc.name;
        },
        callTouch() {
            this.$v.$touch();
        },
        onFocus(e) {
            setTimeout(() => e.target.select(), 0);
            this.$emit('focus');
        },
        onChange() {
            this.$emit('change', this.autocompleteText);
        },
        focus() {
            this.$refs.servicesAutocomplete.$el.focus();
        },
        blur() {
            this.$refs.servicesAutocomplete.$el.blur();
            setTimeout(() => {
                this.isOpen = false;
            }, 300);
        },
        update(value) {
            this.autocompleteText = value;
        },
        setAutocompleteResult(data) {
            if (data != null) {
                this.selectedAutocomplete = data;
                this.autocompleteText = this.getDisplayText(data);
            } else {
                this.selectedAutocomplete = null;
                this.autocompleteText = '';
            }
            const val = Object.assign({}, data);
            this.$emit('input', val);
            this.isOpen = false;
        },
        onArrowDown() {
            if (this.arrowCounter < this.autocompleteList.length - 1) {
                this.arrowCounter += 1;
                this.fixScrolling();
            }
        },
        onArrowUp() {
            if (this.arrowCounter > 0) {
                this.arrowCounter -= 1;
                this.fixScrolling();
            }
        },
        onEnter() {
            this.setAutocompleteResult(this.autocompleteList[this.arrowCounter]);
            this.arrowCounter = -1;
        },
        fixScrolling() {
            const optionHeight = this.$refs.autocompleteOptions[this.arrowCounter].clientHeight;
            this.$refs.autocompleteContainer.scrollTop = optionHeight * this.arrowCounter;
        },
        async getServicesList() {
            this.isAutoCompleteLoading = true;
            const endpoint = `/api/services/search`;
            const {
                data: { services }
            } = await handleRequests(endpoint, {
                params: { pageNumber: 1, itemsPerPage: 50, searchText: this.autocompleteText }
            });
            this.isOpen = true;
            this.autocompleteList = services;
            this.isAutoCompleteLoading = false;
        },
        handleClickOutside(evt) {
            if (!this.$el.contains(evt.target)) {
                if (this.selectedAutocomplete === null) 
                    this.setAutocompleteResult(null);
                else 
                    this.isOpen = false;
                this.arrowCounter = -1;
            }
        },
        async getServiceDetails(serviceId) {
            try {
                const api = `/api/services/${serviceId}`;
                const { data } = await handleRequests(api);
                this.selectedAutocomplete = data;
            } catch (error) {
                const message = 'Error in getting the service details';
                showErrorMessage(this, message, error);
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.services-autocomplete {
    position: relative;
    width: 100%;
}
.autocomplete {
    position: absolute;
    width: 100%;
    background-color: #fff;
    z-index: 9;
    top: 40px;
}
.autocomplete-results {
    padding: 0;
    margin: 0;
    border: 1px solid #eeeeee;
    height: 120px;
    overflow: auto;
    width: 100%;
}
.autocomplete-result {
    list-style: none;
    text-align: left;
    padding: 4px 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
}
.autocomplete-result .name {
    flex: 1;
}
.autocomplete-result.is-active,
.autocomplete-result:hover {
    background-color: #4aae9b;
    color: white;
}
</style>
